import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { DataForCustomFormModel } from '@bo-client/activation-management/shared/models/actionWithMoreInfo.model';
import { GridRegistrationModel } from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CreateGridRegistrationFormsService } from '@bo-client/activation-management/grid-registration/create-grid-registration-forms.service';
import { GridRegistrationTypeEnum } from '@bo-client/activation-management/grid-registration/model/grid-registration.enum';
import { CreateFormsService } from '@bo-client/activation-management/shared/forms/create-forms.service';
import { MetaService } from '@bo-client/core/api/meta-service';
import { CustomFormComponent } from '@bo-client/activation-management/custom-form.component';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { Observable } from 'rxjs';
import { map, shareReplay, startWith, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StorageModel } from '@bo-client/activation-management/grid-registration/model/storage.model';

@Component({
  selector: 'app-gr-custom-approve',
  templateUrl: './gr-custom-approve.component.html',
  styleUrls: ['./gr-custom-approve.component.scss']
})
export class GrCustomApproveComponent
  implements OnInit, CustomFormComponent<GridRegistrationModel, GridRegistrationModel>
{
  private readonly destroyRef = inject(DestroyRef);

  @Input() data: DataForCustomFormModel<GridRegistrationModel>;
  @Output() onConfirm: EventEmitter<GridRegistrationModel> = new EventEmitter();

  model: GridRegistrationModel;
  currentFormApprove: FormGroup;
  gridRegistrationType = GridRegistrationTypeEnum;
  maxExtractionPower = 0;

  isQUStandardCurve$: Observable<boolean>;

  constructor(
    private createFormsGridRegistrationService: CreateGridRegistrationFormsService,
    private createFormsService: CreateFormsService,
    protected metaService: MetaService,
    protected dataShareService: DataShareService
  ) {}

  ngOnInit(): void {
    this.model = this.data.model;
    if (this.model.storages) {
      this.model.storages.forEach((x) => {
        this.maxExtractionPower = this.maxExtractionPower + x.usableCapacity * x.count;
      });
    }
    if (this.model.consumers) {
      this.model.consumers.forEach((x) => {
        this.maxExtractionPower = this.maxExtractionPower + +x.power * +x.count;
      });
    }
    this.currentFormApprove = this.createFormsGridRegistrationService.createGridConnectionPointForm(
      this.data.model,
      this.maxExtractionPower
    );

    this.isQUStandardCurve$ = (
      this.currentFormApprove.controls.reactivePowerProvisioningMethod as FormControl<string>
    ).valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      startWith(this.currentFormApprove.controls.reactivePowerProvisioningMethod.value),
      map((value: string) => value === 'Q_U_STANDARD_CURVE'),
      tap((isStandardCurve: boolean) => {
        const cosPhiControl = this.currentFormApprove.controls.cosPhi;
        if (isStandardCurve) {
          cosPhiControl.clearValidators();
          cosPhiControl.setValue(null);
        } else {
          cosPhiControl.setValidators([Validators.required]);
        }
        cosPhiControl.updateValueAndValidity();
      }),
      shareReplay(1)
    );
  }

  checkboxChanged(event: any) {
    if (event.checked) {
      this.currentFormApprove
        .get('extractionNetworkPowerAuthorized')
        .setValidators([
          Validators.required,
          this.createFormsService.lessThanValue(this.maxExtractionPower),
          this.createFormsService.greaterThanZero
        ]);
      this.currentFormApprove.get('extractionNetworkPowerAuthorized').patchValue('');
      this.currentFormApprove.get('extractionNetworkPowerAuthorized').markAsTouched();
      this.currentFormApprove.get('extractionNetworkPowerAuthorized').enable();
    } else {
      this.currentFormApprove.get('extractionNetworkPowerAuthorized').disable();
    }
  }

  cancel() {
    this.onConfirm.emit(null);
  }

  confirm() {
    if (this.currentFormApprove.valid) {
      this.model = {
        ...this.model,
        ...this.currentFormApprove.value
      };
      this.onConfirm.emit(this.model);
    } else {
      this.currentFormApprove.markAllAsTouched();
    }
  }

  protected hasProduction(storageOperationType: StorageModel['storageOperationType']) {
    return StorageModel.hasProduction(storageOperationType);
  }
}
